import React, { useEffect, useRef, useState } from 'react';
import { MapPin, CheckCircle, X } from 'lucide-react';
import toast from 'react-hot-toast';

const GoogleAddressSelector = ({ 
  onAddressSelect, 
  onClose, 
  initialAddress = null,
  isOpen = false 
}) => {
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const markerRef = useRef(null);
  const [addressData, setAddressData] = useState({
    location: '',
    locality: '',
    administrative_area_level_1: '',
    postal_code: '',
    country: 'India',
    coordinates: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      initializeGoogleMaps();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialAddress) {
      setAddressData({
        location: initialAddress.address || '',
        locality: initialAddress.city || '',
        administrative_area_level_1: initialAddress.state || '',
        postal_code: initialAddress.pincode || '',
        country: initialAddress.country || 'India',
        coordinates: initialAddress.coordinates || null
      });
    }
  }, [initialAddress]);

  const setupMapListeners = () => {
    try {
      // Initialize autocomplete
      if (autocompleteRef.current) {
        autocompleteRef.current.autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteRef.current,
          {
            fields: ['address_components', 'geometry', 'name', 'formatted_address'],
            types: ['address']
          }
        );

        autocompleteRef.current.autocomplete.addListener('place_changed', () => {
          const place = autocompleteRef.current.autocomplete.getPlace();
          if (place.geometry) {
            handlePlaceSelect(place);
          }
        });
      }

      // Add marker drag listener
      if (markerRef.current) {
        markerRef.current.addListener('dragend', () => {
          const position = markerRef.current.getPosition();
          reverseGeocode(position.lat(), position.lng());
        });
      }

      // Add map click listener
      if (mapRef.current.map) {
        mapRef.current.map.addListener('click', (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          markerRef.current.setPosition({ lat, lng });
          reverseGeocode(lat, lng);
        });
      }
    } catch (error) {
      console.error('Error setting up map listeners:', error);
    }
  };

  const reverseGeocode = (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const place = results[0];
          handlePlaceSelect(place);
        } else {
          console.error('Geocoding failed:', status);
        }
      });
    } catch (error) {
      console.error('Error in reverse geocoding:', error);
    }
  };

  const initializeGoogleMaps = async () => {
    try {
      setIsLoading(true);
      
      // Add Google Maps script if not already loaded
      if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBB7eCnq1g88yxps20vrHfiW3QC07G3LPU&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          console.error('Failed to load Google Maps script');
          setIsLoading(false);
          setMapError(true);
          toast.error('Failed to load map. Please try again.');
        };
        document.head.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
      }

      // Initialize map
      if (mapRef.current && !mapRef.current.map) {
        // Try to get user's current location first
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              try {
                const userLat = position.coords.latitude;
                const userLng = position.coords.longitude;
                
                mapRef.current.map = new window.google.maps.Map(mapRef.current, {
                  center: { lat: userLat, lng: userLng },
                  zoom: 16,
                  mapTypeControl: false,
                  streetViewControl: true,
                  fullscreenControl: true,
                  zoomControl: true
                });

                // Initialize marker at user's location
                markerRef.current = new window.google.maps.Marker({
                  position: { lat: userLat, lng: userLng },
                  map: mapRef.current.map,
                  draggable: true,
                  title: 'Your Location'
                });

                // Reverse geocode user's location
                reverseGeocode(userLat, userLng);
                setupMapListeners();
              } catch (error) {
                console.error('Error initializing map with user location:', error);
                initializeMapWithJaipur();
              }
            },
            (error) => {
              console.log('Geolocation error:', error);
              // Fallback to Jaipur if geolocation fails
              initializeMapWithJaipur();
            },
            {
              timeout: 10000,
              enableHighAccuracy: true,
              maximumAge: 300000
            }
          );
        } else {
          // Fallback to Jaipur if geolocation not supported
          initializeMapWithJaipur();
        }
      }

      const initializeMapWithJaipur = () => {
        try {
          mapRef.current.map = new window.google.maps.Map(mapRef.current, {
            center: { lat: 26.9147, lng: 75.7004 }, // Jaipur coordinates
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: true,
            fullscreenControl: true,
            zoomControl: true
          });

          // Initialize marker
          markerRef.current = new window.google.maps.Marker({
            position: { lat: 26.9147, lng: 75.7004 },
            map: mapRef.current.map,
            draggable: true,
            title: 'Delivery Location'
          });

          setupMapListeners();
        } catch (error) {
          console.error('Error initializing map with Jaipur:', error);
          setIsLoading(false);
          toast.error('Failed to initialize map. Please try again.');
        }
      }

    } catch (error) {
      console.error('Error initializing Google Maps:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaceSelect = (place) => {
    try {
      const addressComponents = place.address_components || [];
      const geometry = place.geometry;
      
      if (geometry && geometry.location) {
        const lat = geometry.location.lat();
        const lng = geometry.location.lng();
        
        // Update marker position
        if (markerRef.current) {
          markerRef.current.setPosition({ lat, lng });
        }
        if (mapRef.current.map) {
          mapRef.current.map.setCenter({ lat, lng });
        }
        
        // Parse address components
        const addressData = {
          location: place.formatted_address || '',
          locality: '',
          administrative_area_level_1: '',
          postal_code: '',
          country: 'India',
          coordinates: { lat, lng }
        };

        addressComponents.forEach(component => {
          const types = component.types;
          if (types.includes('locality')) {
            addressData.locality = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            addressData.administrative_area_level_1 = component.long_name;
          } else if (types.includes('postal_code')) {
            addressData.postal_code = component.long_name;
          } else if (types.includes('country')) {
            addressData.country = component.long_name;
          }
        });

        setAddressData(addressData);
      }
    } catch (error) {
      console.error('Error handling place select:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setAddressData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAddress = () => {
    if (addressData.coordinates && addressData.location) {
      const formattedAddress = {
        address: addressData.location,
        city: addressData.locality,
        state: addressData.administrative_area_level_1,
        pincode: addressData.postal_code,
        country: addressData.country,
        coordinates: addressData.coordinates,
        lat: addressData.coordinates.lat,
        lng: addressData.coordinates.lng
      };
      
      onAddressSelect(formattedAddress);
      onClose();
    } else if (addressData.location && addressData.locality && addressData.postal_code) {
      // Allow saving without coordinates if map failed but user entered address manually
      const formattedAddress = {
        address: addressData.location,
        city: addressData.locality,
        state: addressData.administrative_area_level_1,
        pincode: addressData.postal_code,
        country: addressData.country,
        coordinates: null,
        lat: null,
        lng: null
      };
      
      onAddressSelect(formattedAddress);
      onClose();
    } else {
      alert('Please select a valid address on the map or fill in all required fields');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '900px',
        height: '600px',
        display: 'flex',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Address Form Panel */}
        <div style={{
          width: '45%',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e5e7eb',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <MapPin size={20} color="#ff6b35" />
              <h3 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                Select Address
              </h3>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} color="#6b7280" />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Search Address *
              </label>
              <input
                ref={autocompleteRef}
                type="text"
                placeholder="Type to search for address..."
                value={addressData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: 'Inter, sans-serif',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff6b35';
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#fafafa';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                City *
              </label>
              <input
                type="text"
                placeholder="Enter city"
                value={addressData.locality}
                onChange={(e) => handleInputChange('locality', e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: 'Inter, sans-serif',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff6b35';
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#fafafa';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  State *
                </label>
                <input
                  type="text"
                  placeholder="Enter state"
                  value={addressData.administrative_area_level_1}
                  onChange={(e) => handleInputChange('administrative_area_level_1', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'Inter, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fafafa'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#fafafa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <div style={{ width: '120px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Pincode *
                </label>
                <input
                  type="text"
                  placeholder="Pincode"
                  value={addressData.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontFamily: 'Inter, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#fafafa'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff6b35';
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#fafafa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Country *
              </label>
              <input
                type="text"
                placeholder="Enter country"
                value={addressData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontFamily: 'Inter, sans-serif',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#fafafa'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff6b35';
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#fafafa';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '16px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '16px 24px',
                backgroundColor: '#f8f9fa',
                color: '#6b7280',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: 'Inter, sans-serif'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e9ecef';
                e.currentTarget.style.borderColor = '#dee2e6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAddress}
              disabled={!(addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code))}
              style={{
                flex: 1,
                padding: '16px 24px',
                backgroundColor: (addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code)) ? '#ff6b35' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: (addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code)) ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontFamily: 'Inter, sans-serif',
                boxShadow: (addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code)) ? '0 4px 12px rgba(255, 107, 53, 0.3)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code)) {
                  e.currentTarget.style.backgroundColor = '#e55a2b';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 107, 53, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (addressData.coordinates || (addressData.location && addressData.locality && addressData.postal_code)) {
                  e.currentTarget.style.backgroundColor = '#ff6b35';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.3)';
                }
              }}
            >
              <CheckCircle size={18} />
              Save Address
            </button>
          </div>
        </div>

        {/* Map Panel */}
        <div style={{
          width: '55%',
          position: 'relative',
          backgroundColor: '#f8f9fa'
        }}>
          {isLoading ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #ff6b35',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <p style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>Loading Map...</p>
                <p style={{ margin: 0, fontSize: '14px' }}>Detecting your location</p>
              </div>
            </div>
          ) : mapError ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              flexDirection: 'column',
              gap: '16px',
              padding: '32px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MapPin size={40} color="#dc2626" />
              </div>
              <div style={{ textAlign: 'center', color: '#6b7280' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  Map Unavailable
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '14px' }}>
                  Unable to load the map. You can still enter your address manually.
                </p>
                <button
                  onClick={() => {
                    setMapError(false);
                    setIsLoading(true);
                    initializeGoogleMaps();
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '16px',
                right: '16px',
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                padding: '12px 16px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backdropFilter: 'blur(10px)'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937',
                  textAlign: 'center'
                }}>
                  📍 Click on the map or drag the marker to select your exact location
                </p>
              </div>
              <div
                ref={mapRef}
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '0 16px 16px 0'
                }}
              />
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GoogleAddressSelector;
